import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Field, Select, StatusPill } from '@/components/live';
import { callRpc } from '@/api/resources';
import { useAuth } from '@/auth/AuthProvider';
import { can, isAdmin } from '@/auth/session';
import { toast } from '@/lib/toast';
import {
  RECORD_STATUSES, RECORD_STATUS_LABELS, needsReason, type StatusEntity,
} from '@/lib/recordStatus';

/** The readiness test answers in codes; say what they mean. */
const REASONS: Record<string, string> = {
  mandatory_kyc_incomplete: 'a mandatory KYC record is missing or not yet verified',
  mandatory_consent_incomplete: 'a mandatory consent has not been given',
  no_active_party_role: 'the party has no active role',
  party_risk_blocked: 'the party is blocked on risk',
};

function readable(message: string): string {
  return message.replace(/\b[a-z]+(?:_[a-z]+)+\b/g, (code) => REASONS[code] ?? code);
}

/**
 * Move a record between Pending, Verified, Suspended and Terminated.
 *
 * Every rule here is enforced by the database (`authz.fn_record_status_guard`); this component
 * only avoids offering a move the server will refuse. A user without the screen's `approve`
 * right sees the status and nothing to change it with - update rights are not enough, because
 * marking a record Verified is a checking decision, not data entry.
 */
export function StatusChanger({
  entity,
  id,
  current,
  screenKey,
  invalidate,
}: {
  entity: StatusEntity;
  id: string;
  current: string | null | undefined;
  screenKey: string;
  /** Query keys to refresh after a change. */
  invalidate: string[];
}) {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const [next, setNext] = useState(current ?? 'pending');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const mayChange = can(profile, screenKey, 'approve');
  const lockedTerminated = (current === 'terminated' || current === 'withdrawn') && !isAdmin(profile);

  if (!mayChange || lockedTerminated) {
    return (
      <div className="status-changer">
        <StatusPill value={current} />
        <small className="muted">
          {lockedTerminated
            ? `Only an organisation admin can reinstate a ${current} record.`
            : 'Changing the status needs approval rights.'}
        </small>
      </div>
    );
  }

  const reasonNeeded = needsReason(next);
  const unchanged = next === current;

  async function apply() {
    if (reasonNeeded && !reason.trim()) {
      toast.error(`Give a reason for marking this ${RECORD_STATUS_LABELS[next as keyof typeof RECORD_STATUS_LABELS]}.`);
      return;
    }
    setSaving(true);
    try {
      await callRpc('fn_set_record_status', {
        p_entity: entity, p_id: id, p_status: next, p_reason: reason.trim() || null,
      });
      await Promise.all(invalidate.map((key) => queryClient.invalidateQueries({ queryKey: [key] })));
      toast.success(`Marked ${RECORD_STATUS_LABELS[next as keyof typeof RECORD_STATUS_LABELS]}.`);
      setReason('');
    } catch (error) {
      toast.error(error instanceof Error ? readable(error.message) : 'The status could not be changed.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="status-changer">
      <Field label="Status">
        <div className="status-changer-row">
          <Select
            onChange={setNext}
            options={RECORD_STATUSES.map((status) => ({ id: status, label: RECORD_STATUS_LABELS[status] }))}
            value={next}
          />
          <button className="primary" disabled={saving || unchanged} onClick={() => void apply()} type="button">
            {saving ? 'Saving...' : 'Apply'}
          </button>
        </div>
      </Field>
      {reasonNeeded && !unchanged && (
        <Field label="Reason" required>
          <input onChange={(event) => setReason(event.target.value)}
                 placeholder="Recorded on the record and in its audit history" value={reason} />
        </Field>
      )}
    </div>
  );
}
